import { Flame } from 'lucide-react';

/**
 * StreakBanner — home dashboard flame + streak count.
 *
 * Hidden entirely on cold-start (no scans ever). Renders "hot" style when
 * streak >= 3, subdued otherwise.
 *
 * Props:
 *   streak: number         Current consecutive-day streak
 *   longest: number        Longest streak ever
 *   totalScans: number     Lifetime scan count (used only to gate first render)
 */
export default function StreakBanner({ streak, longest, totalScans }) {
  if (!totalScans) return null;

  const hot = streak >= 3;
  const broken = streak === 0;
  const containerCls = hot
    ? 'bg-gradient-to-r from-qc-amber/15 via-qc-amber/10 to-qc-bg border-qc-amber/40'
    : 'bg-qc-surface border-qc-border';
  const iconCls = hot ? 'bg-qc-amber text-qc-bg' : 'bg-qc-surface2 text-qc-text-faint';
  const numCls = hot ? 'text-qc-amber' : broken ? 'text-qc-text-dim' : 'text-qc-text';

  return (
    <div
      className={`relative overflow-hidden rounded-2xl mb-4 p-4 border ${containerCls}`}
      data-testid="streak-banner"
    >
      {hot && (
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-qc-amber/20 blur-3xl pointer-events-none" />
      )}
      <div className="relative flex items-center gap-4">
        <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${iconCls}`}>
          {hot && <div className="absolute inset-0 rounded-2xl bg-qc-amber/40 blur-md animate-green-pulse" />}
          <Flame className={`relative w-7 h-7 ${hot ? 'fill-qc-bg' : ''}`} strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span
              className={`text-3xl font-black tabular-nums tracking-tight ${numCls}`}
              data-testid="streak-value"
            >
              {streak}
            </span>
            <span className="text-base font-bold text-qc-text-dim">day streak</span>
          </div>
          <p className="text-xs text-qc-text-dim mt-0.5">
            {broken
              ? 'Scan today to start a new streak.'
              : hot
              ? `On fire. Best ever: ${longest} ${longest === 1 ? 'day' : 'days'}.`
              : `Keep going — best streak: ${longest} ${longest === 1 ? 'day' : 'days'}.`}
          </p>
        </div>
      </div>
    </div>
  );
}
